import gql from 'graphql-tag';
import React, { useContext, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { growlErrors } from '../../../../utils/NotificationUtil';
import { UPDATE_DOCUMENTS_STATUS_MUTATION } from '../../../../pages/Accounts/DocumentManagement/services/document-management.service';
import { AccountContext } from '../../../../components/AccountContext';
import { NotificationContext } from '../../../../components/GrowlNotifications';
import { buildDocumentsDisplayNames } from '../../../../pages/Accounts/DocumentManagement/helpers/documents.helper';

const ACCOUNT_TO_BE_DELETED_NOTIFICATION_QUERY = gql`
  query accountToBeDeletedNotificationQuery($accountId: ID!) {
    accountToBeDeletedNotification(id: $accountId) {
      declinedDocuments
      documentAcceptanceChangeRequests {
        id
        documentId
        documentType
      }
      numberOfDaysUntilDeletion
      isOwner
      owner {
        firstName
        lastName
        email
      }
    }
  }
`;

const DEFAULT_STATE = {
  declinedDocuments: [],
  documentAcceptanceChangeRequests: [],
  numberOfDaysUntilDeletion: 0,
  isOwner: false,
  owner: {
    firstName: '',
    lastName: '',
    email: '',
  },
};

const AccountToBeDeletedNotification = () => {
  const [notification, setNotification] = useState(DEFAULT_STATE);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const [updateDocumentChangeAcceptanceRequestsStatuses] = useMutation(UPDATE_DOCUMENTS_STATUS_MUTATION);
  const { data } = useQuery(ACCOUNT_TO_BE_DELETED_NOTIFICATION_QUERY, {
    variables: {
      accountId: currentAccount,
    },
    fetchPolicy: 'network-only',
  });

  const updateStatuses = async (inputTypes, callback) => {
    try {
      await updateDocumentChangeAcceptanceRequestsStatuses({
        variables: {
          input: {
            accountId: currentAccount,
            inputTypes,
          },
        },
      });

      callback();
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const acceptDocuments = () => {
    const inputTypes = notification.documentAcceptanceChangeRequests.map((documentAcceptanceChangeRequest) => {
      return {
        id: documentAcceptanceChangeRequest.id,
        documentId: documentAcceptanceChangeRequest.documentId,
        documentType: documentAcceptanceChangeRequest.documentType,
        status: 'ACCEPTED',
      };
    });

    const callback = () => setNotification(DEFAULT_STATE);

    updateStatuses(inputTypes, callback);
  };

  useEffect(() => {
    if (data && data.accountToBeDeletedNotification) {
      setNotification(data.accountToBeDeletedNotification);
    }
  }, [data]);

  const documentsDisplayNames = buildDocumentsDisplayNames(notification.declinedDocuments);

  return (
    <Dialog
      header={`This account is deleted in ${notification.numberOfDaysUntilDeletion} days!`}
      dismissableMask={false}
      closable={false}
      blockScroll={true}
      visible={notification !== DEFAULT_STATE}
      onHide={() => {}}
      position="center"
    >
      {notification.isOwner && (
        <>
          <p>
            {`As you have declined the updated ${documentsDisplayNames}, your organisation's corporate GR8PI platform is deleted 90 days
            after the updated ${documentsDisplayNames} enter(s) into force.`}
          </p>
          <p>
            If you want to reverse your declination, please click ”Accept”. If you want to maintain your declination,
            please click “Continue”.
          </p>
          <div className="center">
            <Button type="button" className="p-button-primary" label="ACCEPT" onClick={acceptDocuments} />
            <Button
              type="button"
              className="p-button-primary"
              label="CONTINUE"
              onClick={() => setNotification(DEFAULT_STATE)}
            />
          </div>
        </>
      )}
      {!notification.isOwner && (
        <>
          <p>Please contact your account owner for more details:</p>
          <p>
            {notification.owner.firstName} {notification.owner.lastName}
          </p>
          <p>{notification.owner.email}</p>
          <Button
            type="button"
            className="p-button-primary"
            label="GOT IT"
            onClick={() => setNotification(DEFAULT_STATE)}
          />
        </>
      )}
    </Dialog>
  );
};

export default AccountToBeDeletedNotification;
